import type { ErrorEvent, EventHint } from '@sentry/react';

import {
  FETCH_EXTENSIONS_SUCCESS,
  FETCH_FLEETBACK_PACK_SUCCESS
} from 'constants/ActionTypes';
import { FETCH_APP_CONFIGURATION_SUCCESS } from 'modules/app/actionTypes';
import { LOAD_SESSION_SUCCESS, SIGN_IN_REQUEST } from 'modules/auth/actionTypes';
import { FETCH_ALL_BRANDS_SUCCESS } from 'modules/brands/actionsType';
import { FETCH_COUNTRIES_SUCCESS } from 'modules/countries/actionTypes';
import { FETCH_TRANSLATIONS_SUCCESS } from 'modules/i18n/actionTypes';
import { CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';

const EXCLUDED_PROPERTIES = ['customerInfo', 'dealer', 'vehicleInfo', 'auditModel', 'accessToken', 'idToken',
  'serviceHeaders', 'productToken'];
const EXCLUDED_SLICES = ['i18n', 'auth', 'users', 'brands', 'medias', 'countries', 'newUsers',
  'mediaLibrary', 'serviceHeaders', 'pack',
  'interventions', 'audit', 'checklists', 'selfServices', 'leads', 'tireScan', 'bodyScan'];
const EXCLUDED_ACTIONS = [SIGN_IN_REQUEST, CURRENT_USER_DATA_SUCCESS, LOAD_SESSION_SUCCESS,
  FETCH_APP_CONFIGURATION_SUCCESS, FETCH_EXTENSIONS_SUCCESS, FETCH_FLEETBACK_PACK_SUCCESS, FETCH_COUNTRIES_SUCCESS,
  FETCH_ALL_BRANDS_SUCCESS, FETCH_TRANSLATIONS_SUCCESS];

const cleanProperties = (data: any): any => {
  if (data && typeof data === 'object') {
    const entries: [string, any][] = [];
    for (const [key, value] of Object.entries(data) as [string, any][]) {
      if (EXCLUDED_PROPERTIES.includes(key)) {
        if (value?.id) {
          entries.push([`${key}Id`, value.id]);
        }
      } else {
        entries.push([key, typeof value === 'object' ? cleanProperties(value) : value]);
      }
    }
    return Object.fromEntries(entries);
  }
  return data;
};

export const stateTransformer = (state: any) => {
  const excludedState = Object.entries(state).reduce((acc, [key, value]) => {
    if (!EXCLUDED_SLICES.includes(key)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  const cleanedState = cleanProperties(excludedState);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { config, ...restApp } = cleanedState.app;

  return { ...cleanedState, app: restApp, currentUserId: state.newUsers.currentUser?.data?.userId };
};

export const actionTransformer = (action: any) => {
  if (EXCLUDED_ACTIONS.includes(action.type)) {
    return { type: action.type };
  }

  if (action.payload) {
    return { ...action, payload: cleanProperties(action.payload) };
  }

  return action;
};

// DOMException.ABORT_ERR — emitted when an HTMLMediaElement.play() promise is interrupted, e.g. when
// the media element is removed from the document (modal close / navigation), a new load/play request
// supersedes the previous one, or a pause() interrupts a pending play(). These rejections are benign
// by the HTML spec (see https://goo.gl/LdLk22) and bubble up as unhandled rejections via react-player,
// which owns the play() call site — so we cannot attach a `.catch()` to the promise itself.
const MEDIA_PLAY_ABORT_MESSAGE = 'The play() request was interrupted';

/**
 * Returns true when the given error is a benign `AbortError` raised by a media element's `play()`
 * promise. Covers every interruption variant (unmount, source change, pause). Drives both the
 * browser-level handler (`unhandledrejection` + `preventDefault`) and the Sentry `beforeSend` filter.
 */
export const isBenignMediaPlayAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { name, message } = error as { name?: unknown; message?: unknown };

  if (name !== 'AbortError') {
    return false;
  }

  if (typeof message !== 'string') {
    return false;
  }

  return message.includes(MEDIA_PLAY_ABORT_MESSAGE);
};

/**
 * `unhandledrejection` listener that genuinely *handles* benign media `play()` AbortErrors:
 * `preventDefault()` marks the rejection as handled and suppresses the browser's console warning.
 * Since react-player owns the `play()` promise, this is the only place we can neutralise the
 * rejection at the browser level (rather than merely hiding it from Sentry after the fact).
 */
export const handleBenignMediaPlayAbortRejection = (event: PromiseRejectionEvent): void => {
  if (isBenignMediaPlayAbortError(event.reason)) {
    event.preventDefault();
  }
};

/**
 * Registers the browser-level handler for benign media `play()` AbortErrors. This complements
 * `beforeSend`: the Sentry SDK captures unhandled rejections through its own
 * `window.onunhandledrejection` hook and does NOT honour `preventDefault`, so a Sentry-side filter
 * is still required to keep the issue stream clean.
 */
export const registerBenignMediaPlayAbortHandler = (): void => {
  window.addEventListener('unhandledrejection', handleBenignMediaPlayAbortRejection);
};

/**
 * Sentry `beforeSend` hook. Drops benign media `play()` `AbortError` rejections so they no longer
 * pollute the issue stream, while letting every other event through unchanged.
 */
export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  if (isBenignMediaPlayAbortError(hint?.originalException)) {
    return null;
  }

  return event;
};
